import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ButtonControl,
  FormControl,
  IconControl,
  Loading,
} from 'spark-core-dx/components';
import { useCoreContext } from 'spark-core-dx/contexts';
import { useGenericManager } from 'spark-core-dx/hooks';
import { AuthManager } from 'spark-core-dx/managers';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import CardGridControl from '../resource/components/CardGridControl';
import WizardFooter from '../resource/components/wizard/WizardFooter';
import {
  APPLICATION_OPTION_CARD_METADATA,
  getFirstMetadata,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
  normalizeSelectedOptionKeys,
} from './clientEntryContracts';
import {
  applyInitialAdministratorDefaults,
  applyAuthenticationProviderSelection,
  buildAuthenticationFormData,
  buildAuthenticationMetadata,
  buildClientInformationMetadata,
  buildCurrentDomainTenantDraft,
  buildInitialAdministratorMetadata,
  buildOnboardingGroupRoleMetadata,
  buildOnboardingOperationRequest,
  canExecuteOnboarding,
  canResumeOnboarding,
  CLIENT_ONBOARDING_DOMAIN_METADATA,
  CLIENT_ONBOARDING_FORM_FIELDS,
  CLIENT_ONBOARDING_GROUP_ROLE_CARD_METADATA,
  CLIENT_ONBOARDING_ITEM_NAMES,
  CLIENT_ONBOARDING_OPERATIONS,
  getAuthenticationFieldNames,
  isGuestOnboardingAuthentication,
  normalizeDomainDraft,
  normalizeGroupRoleMappingDraft,
  normalizeWizardSteps,
  removeDomain,
  removeGroupRoleMapping,
  selectMetadataFields,
  upsertDomain,
  upsertGroupRoleMapping,
} from './clientOnboardingContracts';
import {
  buildClientEntraSetupEmail,
  CLIENT_ENTRA_SETUP_SCRIPT_ITEM_NAME,
  CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
  downloadClientEntraSetupScript,
  isMicrosoftEntraAuthentication,
} from './clientEntraSetupScriptContracts';
import ClientEntraSetupGuidance from './ClientEntraSetupGuidance';

const runWizardOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) {
    throw new Error(fallbackMessage);
  }
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const normalizeDomainApplicationChoices = (choices) => (
  (Array.isArray(choices) ? choices : [])
    .map((choice) => ({
      ApplicationId: String(
        choice?.ApplicationId
        ?? choice?.Value
        ?? choice?.value
        ?? '',
      ).trim(),
      DisplayName: String(
        choice?.DisplayName
        ?? choice?.Text
        ?? choice?.text
        ?? '',
      ).trim(),
    }))
    .filter((choice) => (
      choice.ApplicationId && choice.DisplayName
    ))
);

const InlineRunForm = ({
  metadata,
  fieldNames,
  run,
  onChange,
  groupId,
}) => (
  <FormControl
    metadata={metadata}
    formFields={selectMetadataFields(metadata, fieldNames)}
    itemData={run}
    disableFooter
    disableMinWidth
    groupId={groupId}
    tabLayout="top"
    onChange={(value, fieldName) => onChange(fieldName, value)}
    onSave={() => {}}
  />
);

const StepIntro = ({
  step,
  actions = null,
  description = step.Description,
  displayName = step.DisplayName,
}) => (
  <div className="command-onboarding__step-intro">
    <div>
      <strong>{displayName}</strong>
      <span>{description}</span>
    </div>
    {actions ? (
      <div
        className="command-onboarding__step-actions"
        aria-label={`${step.DisplayName} actions`}
      >
        {actions}
      </div>
    ) : null}
  </div>
);

const ReviewFact = ({ label, value }) => (
  <div className="command-onboarding__review-fact">
    <span>{label}</span>
    <strong>{value || 'Not provided'}</strong>
  </div>
);

const formatStatusValue = (value) => String(value || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .trim();

const getValidationSeverity = (issue) => (
  String(issue?.Severity ?? issue?.severity ?? '').toLowerCase()
    === 'warning'
    ? 'warning'
    : 'error'
);

const ValidationPanel = ({
  issues = [],
  hasValidated = false,
  onValidate = null,
  validateDisabled = false,
}) => {
  const warningCount = issues.filter(
    (issue) => getValidationSeverity(issue) === 'warning',
  ).length;
  const errorCount = issues.length - warningCount;

  return (
    <section
      className="resource-wizard__validation-step"
      aria-label="Onboarding validation"
    >
      <header className="resource-wizard__validation-header command-onboarding__validation-header">
        <div>
          <strong>Onboarding validation</strong>
          <span>Resolve errors before creating the client.</span>
        </div>
        <div className="command-onboarding__validation-actions">
          <div
            className="resource-wizard__validation-counts"
            aria-label="Validation result counts"
          >
            <span className="resource-wizard__validation-count resource-wizard__validation-count--error">
              {errorCount} errors
            </span>
            <span className="resource-wizard__validation-count resource-wizard__validation-count--warning">
              {warningCount} warnings
            </span>
          </div>
          {onValidate ? (
            <ButtonControl
              type="ok-small"
              onClick={onValidate}
              disabled={validateDisabled}
            >
              Validate
            </ButtonControl>
          ) : null}
        </div>
      </header>

      <div className="resource-wizard__validation-results">
        {issues.length === 0 ? (
          <div
            className={`resource-wizard__validation-empty${
              hasValidated
                ? ' resource-wizard__validation-empty--success'
                : ''
            }`}
          >
            {hasValidated
              ? 'Onboarding validation passed with no issues.'
              : 'Onboarding validation has not run yet.'}
          </div>
        ) : issues.map((issue, index) => {
          const severity = getValidationSeverity(issue);
          return (
            <div
              className={`resource-wizard__validation-issue resource-wizard__validation-issue--${severity}`}
              key={`${severity}-${issue.Code}-${issue.FieldName}-${index}`}
            >
              <strong>{severity === 'warning' ? 'Warning' : 'Error'}</strong>
              <span>{issue.Message}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const ClientOnboardingScreen = ({
  screen,
  currentEnvironment,
  onboardingRunId = null,
  onCompleted = null,
}) => {
  const coreContext = useCoreContext();
  const wizardMgr = useGenericManager(
    CLIENT_ONBOARDING_ITEM_NAMES.wizard,
  );
  const runMgr = useGenericManager(
    CLIENT_ONBOARDING_ITEM_NAMES.run,
  );
  const setupScriptMgr = useGenericManager(
    CLIENT_ENTRA_SETUP_SCRIPT_ITEM_NAME,
  );
  const wizardMgrRef = useRef(wizardMgr);
  const runMgrRef = useRef(runMgr);
  const setupScriptMgrRef = useRef(setupScriptMgr);
  const [wizard, setWizard] = useState(null);
  const [run, setRun] = useState(null);
  const [runMetadata, setRunMetadata] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [domainDraft, setDomainDraft] = useState(null);
  const [groupRoleMappingDraft, setGroupRoleMappingDraft] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [entraEmailOpen, setEntraEmailOpen] = useState(false);
  const securityOptionsLoadKeyRef = useRef('');
  const contentRef = useRef(null);
  const entraSetupEmail = useMemo(
    () => buildClientEntraSetupEmail({
      run,
      currentEnvironment,
    }),
    [currentEnvironment, run],
  );

  wizardMgrRef.current = wizardMgr;
  runMgrRef.current = runMgr;
  setupScriptMgrRef.current = setupScriptMgr;

  const applyWizard = useCallback((
    nextWizard,
    nextMessage,
    nextMessageTone = 'success',
  ) => {
    if (!nextWizard?.Run) {
      throw new Error('The onboarding operation returned no durable run.');
    }
    const normalizedRun = applyInitialAdministratorDefaults(
      nextWizard.Run,
    );
    const normalizedWizard = normalizedRun === nextWizard.Run
      ? nextWizard
      : { ...nextWizard, Run: normalizedRun };
    setWizard(normalizedWizard);
    setRun(normalizedRun);
    setSelectedDomainId('');
    setDomainDraft(null);
    setGroupRoleMappingDraft(null);
    setMessage(
      nextMessage !== undefined
        ? nextMessage
        : nextWizard.Result?.Message || '',
    );
    setMessageTone(nextMessageTone);
    return normalizedWizard;
  }, []);

  const load = useCallback(async (onboardingRunId = null) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const [
        loadedWizard,
        metadataResult,
      ] = await Promise.all([
        runWizardOperation(
          wizardMgrRef.current,
          CLIENT_ONBOARDING_OPERATIONS.load,
          {
            OnboardingRunId: onboardingRunId,
            CurrentEnvironment: currentEnvironment,
          },
          'Client onboarding is unavailable.',
        ),
        runMgrRef.current.GetMetadata({}),
      ]);
      setRunMetadata(
        getFirstMetadata(metadataResult, runMgrRef.current),
      );
      applyWizard(loadedWizard);
    } catch (ex) {
      setWizard(null);
      setRun(null);
      setError(ex?.message || 'Client onboarding is unavailable.');
    } finally {
      setBusy(false);
    }
  }, [applyWizard, currentEnvironment]);

  useEffect(() => {
    load(onboardingRunId);
  }, [load, onboardingRunId]);

  const updateRun = useCallback((updater) => {
    setRun((current) => {
      const updated = typeof updater === 'function'
        ? updater(current)
        : updater;
      if (!updated) return current;
      return {
        ...updated,
        RunState: ['Running', 'Completed', 'Cancelled'].includes(updated.RunState)
          ? updated.RunState
          : 'Draft',
        ValidationIssues: [],
      };
    });
    setMessage('');
  }, []);

  const updateRunField = useCallback((fieldName, value) => {
    if (!fieldName) return;
    updateRun((current) => (
      fieldName === 'AuthenticationProvider'
        ? applyAuthenticationProviderSelection(current, value)
        : {
          ...current,
          [fieldName]: value,
        }
    ));
  }, [updateRun]);

  const invoke = useCallback(async (
    operationName,
    sourceWizard = wizard,
    sourceRun = run,
    requestedStepKey = null,
  ) => {
    const request = buildOnboardingOperationRequest({
      wizard: sourceWizard,
      run: sourceRun,
      operationName,
      currentEnvironment,
      requestedStepKey,
    });
    return runWizardOperation(
      wizardMgrRef.current,
      operationName,
      request,
      'The onboarding operation could not be completed.',
    );
  }, [currentEnvironment, run, wizard]);

  const saveDraft = useCallback(async ({
    sourceWizard = wizard,
    sourceRun = run,
    quiet = false,
  } = {}) => {
    const saved = await invoke(
      CLIENT_ONBOARDING_OPERATIONS.start,
      sourceWizard,
      sourceRun,
    );
    return applyWizard(
      saved,
      quiet ? '' : 'The durable onboarding draft was saved.',
    );
  }, [applyWizard, invoke, run, wizard]);

  const generateEntraSetupScript = useCallback(async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const savedWizard = await saveDraft({ quiet: true });
      const scriptItem = await runWizardOperation(
        setupScriptMgrRef.current,
        CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
        {
          OnboardingRunId: savedWizard.OnboardingRunId,
          CurrentEnvironment: currentEnvironment,
          AuthenticationProvider:
            savedWizard.Run?.AuthenticationProvider,
        },
        'The Microsoft Entra setup script could not be generated.',
      );
      downloadClientEntraSetupScript(scriptItem);
      setMessageTone('success');
      setMessage(
        `Downloaded ${scriptItem.FileName}. Use the refresh icon after the client Microsoft Entra administrator completes it.`,
      );
    } catch (ex) {
      setError(
        ex?.message
        || 'The Microsoft Entra setup script could not be generated.',
      );
    } finally {
      setBusy(false);
    }
  }, [currentEnvironment, saveDraft]);

  const refreshEntraSetupStatus = useCallback(async () => {
    if (!run?.OnboardingRunId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const loadedWizard = await runWizardOperation(
        wizardMgrRef.current,
        CLIENT_ONBOARDING_OPERATIONS.load,
        {
          OnboardingRunId: run.OnboardingRunId,
          CurrentEnvironment: currentEnvironment,
        },
        'The Microsoft Entra setup status could not be refreshed.',
      );
      const callbackCompleted = (
        loadedWizard?.Run?.EntraSetupCallbackStatus
        === 'Completed and validated'
      );
      applyWizard(
        loadedWizard,
        callbackCompleted
          ? 'Microsoft Entra setup was securely received and validated.'
          : 'Microsoft Entra setup has not completed yet.',
        callbackCompleted ? 'success' : 'warning',
      );
    } catch (ex) {
      setError(
        ex?.message
        || 'The Microsoft Entra setup status could not be refreshed.',
      );
    } finally {
      setBusy(false);
    }
  }, [
    applyWizard,
    currentEnvironment,
    run?.OnboardingRunId,
  ]);

  const copyEntraSetupEmail = useCallback(async () => {
    try {
      if (!globalThis.navigator?.clipboard?.writeText) {
        throw new Error('Clipboard access is unavailable.');
      }
      await globalThis.navigator.clipboard.writeText(
        entraSetupEmail.clipboardText,
      );
      setEntraEmailOpen(false);
      setMessageTone('success');
      setMessage(
        'The client Microsoft Entra setup email was copied.',
      );
      return { Success: true };
    } catch {
      return {
        Success: false,
        DisplayMessage:
          'The client email could not be copied. Select and copy the displayed subject and body manually.',
      };
    }
  }, [entraSetupEmail]);

  const runCommand = useCallback(async (
    operationName,
    {
      saveFirst = false,
      requestedStepKey = null,
    } = {},
  ) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      let sourceWizard = wizard;
      let sourceRun = run;
      if (saveFirst) {
        sourceWizard = await saveDraft({
          sourceWizard,
          sourceRun,
          quiet: true,
        });
        sourceRun = sourceWizard.Run;
      }
      const result = await invoke(
        operationName,
        sourceWizard,
        sourceRun,
        requestedStepKey,
      );
      const appliedWizard = applyWizard(result);
      if (appliedWizard.Run.RunState === 'Completed') {
        await onCompleted?.(appliedWizard);
      }
      return result;
    } catch (ex) {
      setError(
        ex?.message
        || 'The onboarding operation could not be completed.',
      );
      return null;
    } finally {
      setBusy(false);
    }
  }, [applyWizard, invoke, onCompleted, run, saveDraft, wizard]);
  const validateWizard = useCallback(() => runCommand(
    CLIENT_ONBOARDING_OPERATIONS.validate,
    { saveFirst: true },
  ), [runCommand]);

  const steps = useMemo(
    () => normalizeWizardSteps(wizard?.Steps),
    [wizard?.Steps],
  );
  const activeStep = steps[
    Math.max(0, Math.min(
      steps.length - 1,
      Number(run?.CurrentWizardStep || 1) - 1,
    ))
  ];
  const isCreateClientStep = activeStep?.Key
    === 'create-initialize-verify';
  const canWrite = screen.CanWrite && screen.CanExecute;
  const domains = Array.isArray(run?.RequestedDomains)
    ? run.RequestedDomains
    : [];
  const groupRoleMappings = Array.isArray(run?.EntraGroupRoleMappings)
    ? run.EntraGroupRoleMappings
    : [];
  const availableEntraGroups = Array.isArray(wizard?.AvailableEntraGroups)
    ? wizard.AvailableEntraGroups
    : [];
  const availableRoles = Array.isArray(wizard?.AvailableRoles)
    ? wizard.AvailableRoles
    : [];
  const domainApplicationChoices = useMemo(
    () => normalizeDomainApplicationChoices(
      wizard?.AvailableApplications,
    ),
    [wizard?.AvailableApplications],
  );
  const domainValueDisplayResolvers = useMemo(() => ({
    RegisteredAppId: Object.fromEntries(
      domainApplicationChoices.map((application) => [
        String(application.ApplicationId || '').toLowerCase(),
        application.DisplayName,
      ]),
    ),
  }), [domainApplicationChoices]);
  const resolveDomainCardTitle = useCallback((domain) => {
    const applicationId = String(
      domain?.RegisteredAppId || '',
    ).trim().toLowerCase();
    return domainValueDisplayResolvers.RegisteredAppId[applicationId]
      || 'Application not available';
  }, [domainValueDisplayResolvers]);
  const clientInformationMetadata = useMemo(
    () => buildClientInformationMetadata(runMetadata),
    [runMetadata],
  );
  const authenticationMetadata = useMemo(
    () => buildAuthenticationMetadata(runMetadata),
    [runMetadata],
  );
  const authenticationFormData = useMemo(
    () => buildAuthenticationFormData(run),
    [run],
  );
  const groupRoleMetadata = useMemo(
    () => buildOnboardingGroupRoleMetadata({
      groups: availableEntraGroups,
      roles: availableRoles,
    }),
    [availableEntraGroups, availableRoles],
  );
  const initialAdministratorMetadata = useMemo(
    () => buildInitialAdministratorMetadata(
      runMetadata,
      availableRoles,
    ),
    [availableRoles, runMetadata],
  );
  const groupRoleCardRows = useMemo(() => {
    const roleNames = new Map(availableRoles.map((role) => [
      String(role.RoleCode),
      role.DisplayName || role.RoleCode,
    ]));
    return groupRoleMappings.map((mapping) => ({
      ...mapping,
      RoleDisplayNames: (mapping.RoleCodes || [])
        .map((roleCode) => roleNames.get(String(roleCode)) || roleCode)
        .join(', '),
    }));
  }, [availableRoles, groupRoleMappings]);

  useEffect(() => {
    setError('');
    setMessage('');
  }, [activeStep?.Key]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeStep?.Key]);

  useEffect(() => {
    if (activeStep?.Key !== 'initial-client-administrator') {
      return;
    }
    updateRun((current) => applyInitialAdministratorDefaults(current));
  }, [activeStep?.Key, updateRun]);

  useEffect(() => {
    if (activeStep?.Key !== 'security-permission-baseline' || !run) {
      return;
    }
    if (isGuestOnboardingAuthentication(run.AuthenticationProvider)) {
      return;
    }
    const loadKey = [
      run.OnboardingRunId || 'new',
      run.RunVersion || 'unsaved',
      run.ConfigurationApplicationId || '',
      run.EntraTenantId || 'default',
    ].join(':');
    if (securityOptionsLoadKeyRef.current === loadKey) return;
    securityOptionsLoadKeyRef.current = loadKey;
    runCommand(CLIENT_ONBOARDING_OPERATIONS.loadSecurityOptions);
  }, [
    activeStep?.Key,
    run?.ConfigurationApplicationId,
    run?.EntraTenantId,
    run?.AuthenticationProvider,
    run?.OnboardingRunId,
    run?.RunVersion,
    runCommand,
  ]);
  const selectDomain = (domainId) => {
    const selected = domains.find(
      (domain) => String(domain.UniqueId) === String(domainId),
    );
    setSelectedDomainId(domainId || '');
    setDomainDraft(selected ? { ...selected } : null);
  };

  const saveDomain = (validatedDomain = domainDraft) => {
    if (!validatedDomain) return;
    const updatedDomains = upsertDomain(
      domains,
      validatedDomain,
      currentEnvironment,
    );
    updateRunField('RequestedDomains', updatedDomains);
    setSelectedDomainId('');
    setDomainDraft(null);
  };

  const deleteDomain = (domainId = selectedDomainId) => {
    if (!domainId) return;
    updateRunField(
      'RequestedDomains',
      removeDomain(domains, domainId),
    );
    setSelectedDomainId('');
    setDomainDraft(null);
  };

  const addCurrentDomainAndTenant = () => {
    const identityClaims = AuthManager.getIdTokenClaims?.() || {};
    const draft = buildCurrentDomainTenantDraft({
      domains,
      coreContext,
      currentEnvironment,
      registeredAppId:
        run?.ConfigurationApplicationId
        || coreContext?.RoutingConfig?.AppId,
      signedInTenantId: identityClaims.tid,
    });
    if (!draft) {
      setError(
        'The current application domain could not be resolved.',
      );
      return;
    }
    saveDomain(draft);
  };

  const selectGroupRoleMapping = (mappingId) => {
    const selected = groupRoleMappings.find(
      (mapping) => String(mapping.UniqueId) === String(mappingId),
    );
    setGroupRoleMappingDraft(selected ? { ...selected } : null);
  };

  const saveGroupRoleMapping = (
    validatedMapping = groupRoleMappingDraft,
  ) => {
    if (!validatedMapping) return;
    updateRunField(
      'EntraGroupRoleMappings',
      upsertGroupRoleMapping(
        groupRoleMappings,
        validatedMapping,
        availableEntraGroups,
      ),
    );
    setGroupRoleMappingDraft(null);
  };

  const deleteGroupRoleMapping = (mappingId) => {
    updateRunField(
      'EntraGroupRoleMappings',
      removeGroupRoleMapping(groupRoleMappings, mappingId),
    );
    setGroupRoleMappingDraft(null);
  };

  const renderStep = (step) => {
    const isAuthenticationStep = step.Key === 'client-configuration-overrides';
    let displayName = step.DisplayName;
    if (step.Key === 'client-shell') displayName = 'Client Information';
    if (isAuthenticationStep) displayName = 'Authentication';
    const intro = (
      <StepIntro
        step={step}
        displayName={displayName}
        description={isAuthenticationStep
          ? "Configure the client's Microsoft Entra Read / Login connection using the same protected connection contract as Client Authentication."
          : step.Description}
      />
    );

    if (step.Key === 'client-shell') {
      return (
        <>
          {intro}
          <InlineRunForm
            metadata={clientInformationMetadata}
            fieldNames={CLIENT_ONBOARDING_FORM_FIELDS[step.Key]}
            run={run}
            onChange={updateRunField}
            groupId="clientOnboardingShell"
          />
        </>
      );
    }

    if (step.Key === 'application-options') {
      const applicationOptions = Array.isArray(
        wizard?.AvailableApplicationOptions,
      )
        ? wizard.AvailableApplicationOptions
        : [];
      const selectedOptionKeys = normalizeSelectedOptionKeys(
        run?.SelectedApplicationOptionKeys,
      );
      return (
        <>
          {intro}
          <CardGridControl
            className="command-entry__options-card-grid"
            rows={applicationOptions}
            metadata={APPLICATION_OPTION_CARD_METADATA}
            cardFields={APPLICATION_OPTION_CARD_METADATA.Fields}
            rowKey={(option) => option.Key}
            selectedRowKeys={selectedOptionKeys}
            onSelectedRowKeysChange={(keys) => updateRunField(
              'SelectedApplicationOptionKeys',
              normalizeSelectedOptionKeys(keys),
            )}
            selectable
            multiSelect
            readOnly={!canWrite}
            activeFilterEnabled={false}
            selectableRowDisabledResolver={(option) => (
              option.IsSelectable === false
            )}
            emptyContent="No Application Options are available."
            minCardWidth="16rem"
            maxColumns={Math.min(
              2,
              Math.max(1, applicationOptions.length),
            )}
          />
        </>
      );
    }

    if (step.Key === 'domains') {
      return (
        <>
          <StepIntro
            step={step}
            actions={(
              <>
                <IconControl
                  iconKey={coreContext?.Icons?.add_link
                    ? 'add_link'
                    : 'domain'}
                  title="Add Current Domain and Tenant"
                  aria-label="Add Current Domain and Tenant"
                  width="1rem"
                  height="1rem"
                  onClick={addCurrentDomainAndTenant}
                  disabled={!canWrite || !!domainDraft}
                />
                <IconControl
                  iconKey="add"
                  title="Add Domain"
                  aria-label="Add Domain"
                  width="1rem"
                  height="1rem"
                  onClick={() => {
                    const draft = normalizeDomainDraft(
                      {},
                      currentEnvironment,
                      run?.ConfigurationApplicationId,
                    );
                    setSelectedDomainId(draft.UniqueId);
                    setDomainDraft(draft);
                  }}
                  disabled={!canWrite || !!domainDraft}
                />
              </>
            )}
          />
          {!domainDraft ? (
            <CardGridControl
              rows={domains}
              metadata={CLIENT_ONBOARDING_DOMAIN_METADATA}
              cardFields={['RegisteredAppId', 'Domain']
                .map((fieldName) => (
                  CLIENT_ONBOARDING_DOMAIN_METADATA.Fields.find(
                    (field) => field.FieldName === fieldName,
                  )
                ))
                .filter(Boolean)}
              valueDisplayResolvers={domainValueDisplayResolvers}
              rowKey={(domain) => domain.UniqueId}
              cardTitleResolver={resolveDomainCardTitle}
              headerIconActions={[
                {
                  key: 'edit-domain',
                  title: 'Edit Domain',
                  iconKey: 'edit',
                  disabled: !canWrite,
                  onClick: (_domain, domainId) => selectDomain(domainId),
                },
                {
                  key: 'remove-domain',
                  title: 'Remove Domain',
                  iconKey: 'delete',
                  tone: 'danger',
                  disabled: !canWrite,
                  onClick: (_domain, domainId) => deleteDomain(domainId),
                },
              ]}
              disableInlineEdit
              disableCardClickEdit
              activeFilterEnabled={false}
              emptyContent="No domains have been added to this draft."
              minCardWidth="15rem"
            />
          ) : null}
          {domainDraft ? (
            <div className="command-onboarding__workspace-editor">
              <FormControl
                metadata={CLIENT_ONBOARDING_DOMAIN_METADATA}
                formFields={CLIENT_ONBOARDING_DOMAIN_METADATA.Fields}
                itemData={domainDraft}
                disableMinWidth
                tabLayout="top"
                onChange={(value, fieldName) => setDomainDraft((current) => ({
                  ...current,
                  [fieldName]: value,
                  Environment: currentEnvironment,
                }))}
                onCancel={() => {
                  setSelectedDomainId('');
                  setDomainDraft(null);
                }}
                onSave={saveDomain}
                saveButtonText="Save Domain"
              />
            </div>
          ) : null}
        </>
      );
    }

    if (step.Key === 'client-configuration-overrides') {
      const entraSetupComplete = (
        run?.EntraSetupCallbackStatus
        === 'Completed and validated'
      );
      return (
        <>
          {intro}
          <InlineRunForm
            metadata={authenticationMetadata}
            fieldNames={getAuthenticationFieldNames(
              run?.AuthenticationProvider,
            )}
            run={authenticationFormData}
            onChange={updateRunField}
            groupId="clientOnboardingEntraReadLogin"
          />
          {isMicrosoftEntraAuthentication(
            run?.AuthenticationProvider,
          ) ? (
            <ClientEntraSetupGuidance
              canWrite={canWrite}
              busy={busy}
              setupComplete={entraSetupComplete}
              onClientEmail={() => setEntraEmailOpen(true)}
              onEntraSetup={generateEntraSetupScript}
              onRefresh={refreshEntraSetupStatus}
              refreshEnabled={Boolean(run?.OnboardingRunId)}
              statusMessage={run?.EntraSetupCallbackStatus}
              tokenExpiresAtUtc={run?.EntraSetupTokenExpiresAtUtc}
            />
          ) : (
            <div className="command-onboarding__guidance-note">
              Convoke-hosted Guest access uses the existing environment
              readiness, Manage Guest Access, and Guest invitation workflows.
              Customer Entra tenant and application credentials are not
              required here.
            </div>
          )}
        </>
      );
    }

    if (step.Key === 'initial-client-administrator') {
      return (
        <>
          <StepIntro
            step={step}
            description={`Plan the first administrator using ${
              run?.AuthenticationProvider || 'the selected default authentication'
            } without collecting a password or temporary credential.`}
          />
          <InlineRunForm
            metadata={initialAdministratorMetadata}
            fieldNames={CLIENT_ONBOARDING_FORM_FIELDS[step.Key]}
            run={run}
            onChange={updateRunField}
            groupId="clientOnboardingAdministrator"
          />
        </>
      );
    }

    if (step.Key === 'security-permission-baseline') {
      if (isGuestOnboardingAuthentication(run?.AuthenticationProvider)) {
        return (
          <>
            <StepIntro
              step={step}
              displayName="Setup Initial Role Mappings (Optional)"
              description="Entra group mappings do not apply to Guest / External Access. Assign Guest roles through User Profiles and invitations."
            />
          </>
        );
      }
      const securityOptionsReady = (
        wizard?.SecurityOptionsStatus === 'Ready'
      );
      return (
        <>
          <StepIntro
            step={step}
            displayName="Setup Initial Role Mappings (Optional)"
            description="Optionally map groups from the configured client Entra directory to one or more roles in the current application."
            actions={(
              <>
                <IconControl
                  iconKey="refresh"
                  title="Refresh Entra Groups and Roles"
                  aria-label="Refresh Entra Groups and Roles"
                  width="1rem"
                  height="1rem"
                  onClick={() => runCommand(
                    CLIENT_ONBOARDING_OPERATIONS.loadSecurityOptions,
                  )}
                  disabled={busy}
                />
                <IconControl
                  iconKey="add"
                  title="Add Group Role Mapping"
                  aria-label="Add Group Role Mapping"
                  width="1rem"
                  height="1rem"
                  onClick={() => {
                    const draft = normalizeGroupRoleMappingDraft(
                      {},
                      availableEntraGroups,
                    );
                    setGroupRoleMappingDraft(draft);
                  }}
                  disabled={
                    !canWrite
                    || !securityOptionsReady
                    || !!groupRoleMappingDraft
                  }
                />
              </>
            )}
          />
          {!groupRoleMappingDraft ? (
            <CardGridControl
              rows={groupRoleCardRows}
              metadata={CLIENT_ONBOARDING_GROUP_ROLE_CARD_METADATA}
              cardFields={CLIENT_ONBOARDING_GROUP_ROLE_CARD_METADATA.Fields}
              rowKey={(mapping) => mapping.UniqueId}
              cardTitleResolver={(mapping) => (
                mapping.EntraGroupDisplayName || mapping.EntraGroupId
              )}
              headerIconActions={[
                {
                  key: 'edit-group-role-mapping',
                  title: 'Edit Group Role Mapping',
                  iconKey: 'edit',
                  disabled: !canWrite || !securityOptionsReady,
                  onClick: (_mapping, mappingId) => (
                    selectGroupRoleMapping(mappingId)
                  ),
                },
                {
                  key: 'remove-group-role-mapping',
                  title: 'Remove Group Role Mapping',
                  iconKey: 'delete',
                  tone: 'danger',
                  disabled: !canWrite,
                  onClick: (_mapping, mappingId) => (
                    deleteGroupRoleMapping(mappingId)
                  ),
                },
              ]}
              disableInlineEdit
              disableCardClickEdit
              activeFilterEnabled={false}
              emptyContent={
                wizard?.SecurityOptionsStatus === 'ConnectionRequired'
                  ? 'Configure the client Entra Read / Login connection before mapping groups.'
                  : 'No client Entra group mappings have been added.'
              }
              minCardWidth="15rem"
            />
          ) : null}
          {groupRoleMappingDraft ? (
            <div className="command-onboarding__workspace-editor">
              <FormControl
                metadata={groupRoleMetadata}
                formFields={groupRoleMetadata.Fields}
                itemData={groupRoleMappingDraft}
                disableMinWidth
                tabLayout="top"
                onChange={(value, fieldName) => (
                  setGroupRoleMappingDraft((current) => (
                    normalizeGroupRoleMappingDraft(
                      {
                        ...current,
                        [fieldName]: value,
                      },
                      availableEntraGroups,
                    )
                  ))
                )}
                onCancel={() => {
                  setGroupRoleMappingDraft(null);
                }}
                onSave={saveGroupRoleMapping}
                saveButtonText="Save Mapping"
              />
            </div>
          ) : null}
        </>
      );
    }

    if (step.Key === 'database-partition') {
      const partitionFieldNames = CLIENT_ONBOARDING_FORM_FIELDS[step.Key]
        .filter((fieldName) => (
          fieldName === 'PartitionMode'
          || (
            run?.PartitionMode === 'Dedicated'
            && (
              fieldName !== 'PartitionDedicatedMongoDbConnectionString'
              || run?.PartitionConnectionStrategy === 'DedicatedConnection'
            )
          )
        ));
      const connectionSummary = run?.PartitionMode === 'Dedicated'
        && run?.PartitionConnectionStrategy === 'DedicatedConnection'
        ? (
          run?.PartitionDedicatedMongoDbConnectionString
            ? 'Dedicated connection configured'
            : 'Dedicated connection required'
        )
        : 'PlatformApp MongoDB server';

      return (
        <>
          {intro}
          <InlineRunForm
            metadata={runMetadata}
            fieldNames={partitionFieldNames}
            run={run}
            onChange={updateRunField}
            groupId="clientOnboardingPartition"
          />
          {run?.PartitionMode === 'Dedicated' ? (
            <div className="command-onboarding__derived">
              <ReviewFact label="Immutable Partition Key" value={run?.ProposedPartitionKey} />
              <ReviewFact label="Expected Database" value={run?.ExpectedPartitionDatabaseName} />
              <ReviewFact label="Connection" value={connectionSummary} />
            </div>
          ) : null}
          <p className="command-onboarding__phase-note">
            {run?.PartitionMode === 'Dedicated'
              ? 'Shared routing remains active until the dedicated partition is prepared, validated, and explicitly activated.'
              : 'Shared uses the current PlatformApp MongoDB configuration; no client-specific connection is required.'}
          </p>
        </>
      );
    }

    if (step.Key === 'review-validate') {
      return (
        <>
          {intro}
          <div className="command-onboarding__review-grid">
            <ReviewFact label="Client" value={run?.DisplayName || run?.Name} />
            <ReviewFact label="Short Name" value={run?.ShortName} />
            <ReviewFact
              label="Application Options"
              value={`${normalizeSelectedOptionKeys(
                run?.SelectedApplicationOptionKeys,
              ).length}`}
            />
            <ReviewFact label="Domains" value={`${domains.length}`} />
            <ReviewFact label="Authentication" value={`${run?.AuthenticationProvider} / ${run?.AuthenticationAccessMode}`} />
            <ReviewFact label="Initial Administrator" value={run?.InitialAdministratorEmail} />
            <ReviewFact
              label="Initial Administrator Roles"
              value={(run?.InitialAdministratorRoleCodes || []).join(', ')}
            />
            <ReviewFact
              label="Entra Group Role Mappings"
              value={`${groupRoleMappings.length}`}
            />
            <ReviewFact label="Storage Mode" value={run?.PartitionMode} />
          </div>
          {run?.ExistingClientMatchId ? (
            <InlineRunForm
              metadata={runMetadata}
              fieldNames={CLIENT_ONBOARDING_FORM_FIELDS[step.Key]}
              run={run}
              onChange={updateRunField}
              groupId="clientOnboardingReconciliation"
            />
          ) : null}
          <ValidationPanel
            issues={run?.ValidationIssues}
            hasValidated={Boolean(run?.ValidatedAtUtc)}
            onValidate={validateWizard}
            validateDisabled={busy || !canWrite}
          />
        </>
      );
    }

    if (step.Key === 'create-initialize-verify') {
      const creationReady = canExecuteOnboarding(run);
      const creationResumable = canResumeOnboarding(run);
      const creationState = run?.RunState === 'Completed'
        ? 'Client created'
        : run?.RunState === 'Running'
          ? 'Creating client'
          : creationResumable
            ? 'Client creation needs attention'
            : creationReady
              ? 'Ready for creation'
              : 'Not ready for creation';
      const creationMessage = run?.RunState === 'Completed'
        ? 'The client creation run completed.'
        : run?.RunState === 'Running'
          ? 'Client creation is running. The durable result will appear when the operation checkpoints.'
          : creationResumable
            ? 'Review the reported issue below, then resume client creation when the blocker is resolved.'
            : creationReady
              ? 'Review the gathered information below, then select Create Client.'
              : 'Return to Review and Validate before creating the client.';
      const domainSummary = domains.length
        ? domains
          .map((domain) => domain?.Domain)
          .filter(Boolean)
          .join(', ')
        : 'No domains provided';
      const roleNameByCode = new Map(availableRoles.map((role) => [
        String(role?.RoleCode || '').toLowerCase(),
        role?.DisplayName || role?.RoleCode,
      ]));
      const administratorRoles = (run?.InitialAdministratorRoleCodes || [])
        .map((roleCode) => (
          roleNameByCode.get(String(roleCode).toLowerCase()) || roleCode
        ))
        .join(', ');
      const applicationNameById = new Map(
        domainApplicationChoices.map((application) => [
          String(application?.ApplicationId || '').toLowerCase(),
          application?.DisplayName,
        ]),
      );
      const administratorApplications = (
        run?.InitialAdministratorApplicationIds || []
      )
        .map((applicationId) => (
          applicationNameById.get(String(applicationId).toLowerCase())
          || 'Application not available'
        ))
        .join(', ');
      const invitationSummary = [
        formatStatusValue(run?.InvitationMethod),
        formatStatusValue(run?.InvitationTiming),
      ].filter(Boolean).join(' · ');

      return (
        <>
          <StepIntro
            step={step}
            displayName="Create Client"
            description="Review the final client information and create the client when everything is ready."
          />
          {(run?.ValidationIssues?.length > 0 ||
            (!run?.ValidatedAtUtc && !creationReady)) ? (
              <ValidationPanel
                issues={run?.ValidationIssues}
                hasValidated={Boolean(run?.ValidatedAtUtc)}
              />
            ) : null}
          <section
            className="command-onboarding__creation-summary"
            aria-label="Client creation summary"
          >
            <header className="command-onboarding__creation-header">
              <div>
                <strong>{creationState}</strong>
                <span>{creationMessage}</span>
              </div>
              <span
                className={`command-onboarding__creation-state command-onboarding__creation-state--${String(run?.RunState || 'draft').toLowerCase()}`}
              >
                {formatStatusValue(run?.RunState || 'Draft')}
              </span>
            </header>

            <div className="command-onboarding__creation-groups">
              <section className="command-onboarding__creation-group">
                <h3>Client</h3>
                <div className="command-onboarding__review-grid">
                  <ReviewFact label="Client" value={run?.DisplayName || run?.Name} />
                  <ReviewFact label="Short Name" value={run?.ShortName} />
                  <ReviewFact label="Client Identifier" value={run?.ClientIdentifier} />
                  <ReviewFact label="Environment" value={run?.DisplayEnvironment || run?.CurrentEnvironment} />
                </div>
              </section>

              <section className="command-onboarding__creation-group">
                <h3>Initial Administrator</h3>
                <div className="command-onboarding__review-grid">
                  <ReviewFact label="Email" value={run?.InitialAdministratorEmail} />
                  <ReviewFact label="Sign-in Name" value={run?.InitialAdministratorUserName} />
                  <ReviewFact label="Roles" value={administratorRoles} />
                  <ReviewFact label="Applications" value={administratorApplications} />
                  <ReviewFact
                    label="Invitation"
                    value={invitationSummary}
                  />
                </div>
              </section>

              <section className="command-onboarding__creation-group">
                <h3>Routing and Configuration</h3>
                <div className="command-onboarding__review-grid">
                  <ReviewFact label="Domains" value={domainSummary} />
                  <ReviewFact
                    label="Authentication"
                    value={`${run?.AuthenticationProvider} / ${run?.AuthenticationAccessMode}`}
                  />
                  {!isGuestOnboardingAuthentication(
                    run?.AuthenticationProvider,
                  ) ? (
                    <ReviewFact
                      label="Entra Tenant"
                      value={run?.EntraTenantDomain || run?.EntraTenantId}
                    />
                  ) : null}
                  <ReviewFact
                    label="Group Role Mappings"
                    value={`${groupRoleMappings.length}`}
                  />
                  <ReviewFact
                    label="Application Options"
                    value={`${normalizeSelectedOptionKeys(
                      run?.SelectedApplicationOptionKeys,
                    ).length}`}
                  />
                  <ReviewFact label="Storage Mode" value={run?.PartitionMode} />
                </div>
              </section>
            </div>
          </section>
        </>
      );
    }

    return intro;
  };

  const activeStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.Key === activeStep?.Key),
  );
  const moveWizard = (direction) => {
    const nextIndex = Math.max(
      0,
      Math.min(steps.length - 1, activeStepIndex + direction),
    );
    setRun((current) => (
      current
        ? {
          ...current,
          CurrentWizardStep: nextIndex + 1,
        }
        : current
    ));
    setMessage('');
  };
  const continueWizard = async () => {
    if (activeStep?.Key === 'review-validate') {
      const validated = await validateWizard();
      if (validated?.Run?.RunState === 'Validated') {
        moveWizard(1);
      }
      return;
    }

    if (activeStep?.Key === 'create-initialize-verify') {
      if (canExecuteOnboarding(run)) {
        await runCommand(CLIENT_ONBOARDING_OPERATIONS.execute);
      } else if (canResumeOnboarding(run)) {
        await runCommand(CLIENT_ONBOARDING_OPERATIONS.resume);
      }
      return;
    }

    const saved = await runCommand(CLIENT_ONBOARDING_OPERATIONS.start);
    if (!saved) return;
    moveWizard(1);
  };
  const primaryActionLabel = activeStep?.Key === 'create-initialize-verify'
    ? canExecuteOnboarding(run)
      ? 'Create Client'
      : canResumeOnboarding(run)
        ? 'Resume Client Creation'
        : 'Create Client'
    : 'Next >>';
  const createClientActionUnavailable = isCreateClientStep
    && !canExecuteOnboarding(run)
    && !canResumeOnboarding(run);
  const messageIsValidationError = Boolean(
    message
    && wizard?.Result?.Operation === CLIENT_ONBOARDING_OPERATIONS.validate
    && run?.ValidationIssues?.some(
      (issue) => getValidationSeverity(issue) === 'error',
    ),
  );

  return (
    <div className="resource-wizard command-onboarding">
      <div
        className="resource-wizard__content vertical-scrollable"
        ref={contentRef}
      >
        <div className="resource-wizard__identity-topline command-onboarding__status-header">
          <div className="command-onboarding__status-title" role="status">
            {run?.DisplayName || run?.Name || 'New client onboarding'}
            {' · '}
            {run?.RunState || 'Draft'}
            {' · '}
            {isCreateClientStep
              ? canExecuteOnboarding(run)
                ? 'Ready to Create'
                : 'Client Creation'
              : `Step ${run?.CurrentWizardStep || 1} of ${steps.length}`}
          </div>
          {!isCreateClientStep ? (
            <div className="command-onboarding__status-actions">
              <IconControl
                iconKey="preview"
                title="View Run Details"
                aria-label="View Run Details"
                disabled={!run}
                onClick={() => setDetailsOpen(true)}
              />
            </div>
          ) : null}
        </div>

        {busy ? (
          <div className="command-entry__loading" role="status">
            <Loading />
            <span>Working with the durable onboarding run...</span>
          </div>
        ) : null}
        {error ? (
          <div className="resource-wizard__alert" role="alert">
            {error}
          </div>
        ) : null}
        {message ? (
          <div
            className={[
              'resource-wizard__alert',
              messageIsValidationError
                ? ''
                : `resource-wizard__alert--${messageTone}`,
            ].filter(Boolean).join(' ')}
            role={messageIsValidationError ? 'alert' : 'status'}
          >
            {message}
          </div>
        ) : null}

        {!busy && run ? (
          <section
            className="resource-wizard__step-panel"
            aria-label={
              isCreateClientStep
                ? 'Client creation review'
                : 'Client onboarding steps'
            }
          >
            {renderStep(activeStep)}
          </section>
        ) : null}
      </div>

      {!busy && run ? (
        <WizardFooter
          canCancelSetupFlow={false}
          showPreviousStepButton={activeStepIndex > 0}
          showSaveButton={!isCreateClientStep}
          showCompleteAction={false}
          hidePrimaryAction={false}
          isLastStep={false}
          isComplete={false}
          saving={busy}
          hasActiveSectionStep={Boolean(activeStep)}
          activeSectionStepReadOnly={!canWrite}
          nextDisabled={
            busy ||
            !canWrite ||
            !activeStep ||
            createClientActionUnavailable
          }
          primaryActionLabel={primaryActionLabel}
          onPrevious={() => moveWizard(-1)}
          onSave={() => runCommand(CLIENT_ONBOARDING_OPERATIONS.start)}
          onNext={continueWizard}
        />
      ) : null}

      <DialogControl
        openDialog={entraEmailOpen}
        title="Client Microsoft Entra setup email"
        subTitle="Review the client-ready instructions, then copy the subject and body into an approved secure email."
        okText="Copy Email"
        cancelText="Close"
        onCancel={() => setEntraEmailOpen(false)}
        onX={() => setEntraEmailOpen(false)}
        onOk={copyEntraSetupEmail}
      >
        <div className="command-onboarding__entra-email">
          <section>
            <strong>Subject</strong>
            <div>{entraSetupEmail.subject}</div>
          </section>
          <section>
            <strong>Body</strong>
            <pre>{entraSetupEmail.body}</pre>
          </section>
        </div>
      </DialogControl>

      <DialogControl
        openDialog={detailsOpen}
        title="Onboarding run details"
        subTitle="Review the saved onboarding run's status, last save time, retry count, and final readiness."
        okText="Close"
        cancelText=""
        onCancel={() => setDetailsOpen(false)}
        onX={() => setDetailsOpen(false)}
        onOk={() => setDetailsOpen(false)}
      >
        <div className="command-onboarding__details">
          <ReviewFact label="State" value={run?.RunState} />
          <ReviewFact label="Last Saved" value={run?.LastSavedAtUtc} />
          <ReviewFact label="Retry Count" value={`${run?.RetryCount || 0}`} />
          <ReviewFact
            label="Final Readiness"
            value={formatStatusValue(run?.FinalReadiness)}
          />
        </div>
      </DialogControl>
    </div>
  );
};

export default ClientOnboardingScreen;
